/**
 * useDatiQuestionario — tutto ciò che serve alla pagina di un questionario: la definizione, le
 * risposte, le visite della pagina pubblica e i lead di chi ha risposto.
 *
 * Le visite (`platform_questionnaire_sessioni`) le scrive solo il server: se le regole non ne
 * permettono ancora la lettura, la pagina mostra le statistiche senza le aperture e lo dice
 * (`erroreSessioni`), invece di fallire tutta.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { contaNeiConti, normalizzaQuestionario, type Questionario } from '@shared/questionari';
import {
  getLeadsByIds, getQuestionnaire, listResponsesForQuestionnaire, listSessionsForQuestionnaire,
} from '../../../services/platformCrmService';
import type {
  PlatformLead, PlatformQuestionnaire, PlatformQuestionnaireResponse, PlatformQuestionnaireSessione,
} from '../../../types/platformCrm';

export interface DatiQuestionario {
  questionario: PlatformQuestionnaire | null;
  /** La definizione normalizzata (opzioni come oggetti), nella lingua sorgente. */
  normalizzato: Questionario | null;
  risposte: PlatformQuestionnaireResponse[];
  /** null = non lette (vedi `erroreSessioni`). */
  sessioni: PlatformQuestionnaireSessione[] | null;
  erroreSessioni: string | null;
  lead: Map<string, PlatformLead>;
  caricamento: boolean;
  errore: string | null;
  ricarica: () => void;
}

export function useDatiQuestionario(id: string | undefined): DatiQuestionario {
  const [questionario, setQuestionario] = useState<PlatformQuestionnaire | null>(null);
  const [risposte, setRisposte] = useState<PlatformQuestionnaireResponse[]>([]);
  const [sessioni, setSessioni] = useState<PlatformQuestionnaireSessione[] | null>(null);
  const [erroreSessioni, setErroreSessioni] = useState<string | null>(null);
  const [lead, setLead] = useState<Map<string, PlatformLead>>(new Map());
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [giro, setGiro] = useState(0);

  const ricarica = useCallback(() => setGiro((g) => g + 1), []);

  useEffect(() => {
    if (!id) return;
    let annullato = false;
    setCaricamento(true);
    setErrore(null);
    (async () => {
      try {
        const [q, r] = await Promise.all([getQuestionnaire(id), listResponsesForQuestionnaire(id)]);
        if (annullato) return;
        if (!q) {
          setErrore('Questionario non trovato.');
          setQuestionario(null);
          return;
        }
        setQuestionario(q);
        setRisposte(r);

        // Le visite: un permesso mancante non deve fermare il resto della pagina.
        try {
          const s = await listSessionsForQuestionnaire(id);
          if (!annullato) { setSessioni(s); setErroreSessioni(null); }
        } catch (e) {
          console.error('[platform-crm] lettura delle visite del questionario fallita', e);
          if (!annullato) {
            setSessioni(null);
            setErroreSessioni(e instanceof Error ? e.message : 'lettura non riuscita');
          }
        }

        const idLead = r.filter(contaNeiConti).map((x) => x.platform_lead_id);
        const mappa = await getLeadsByIds(idLead);
        if (!annullato) setLead(mappa);
      } catch (e) {
        console.error('[platform-crm] caricamento del questionario fallito', e);
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore nel caricamento del questionario');
      } finally {
        if (!annullato) setCaricamento(false);
      }
    })();
    return () => { annullato = true; };
  }, [id, giro]);

  const normalizzato = useMemo(() => (questionario ? normalizzaQuestionario(questionario) : null), [questionario]);

  return {
    questionario,
    normalizzato,
    risposte,
    sessioni,
    erroreSessioni,
    lead,
    caricamento,
    errore,
    ricarica,
  };
}
